import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxBorder, BoxRadius } from '@genial/design-tokens';
import { Box, Pressable } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import {
  TableContent,
  TableContentItemCaptions,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useFixedIncomeAssetContext } from '../../../../../contexts/products/FixedIncome';
import { useFixedIncomeAssetController } from '../../../../../hooks/products/FixedIncome';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemDesktop } from '../ListLoadingItem/desktop';
import {
  ApplicationInfo,
  FeeInfo,
  MaturityInfo,
  NetValueInfo,
  ProfitabilityInfo,
  RiskInfo,
  TaxInfo,
  UnitPriceInfo,
} from '../shared';

import { FixedIncomeListItemTypes } from '.';

export const FixedIncomeListItemDesktop: React.FC<FixedIncomeListItemTypes.Props> = ({ offer }) => {
  const id = offer.productIdNormalized;
  const {
    control,
    currentValue,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    helpTextProps,
    isFocused,
    showMessageAdjustUnitPrice,
    productsDetailText,
    currentValueFormatted,
    ableToChangeFees,
    selectedFee,
    selectedFeeId,
    setSelectedFeeId,
    minApplication,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  } = useFixedIncomeAssetController(offer);
  const { setModalOpen, setOfferSelected, setOfferFeeSelected } = useFixedIncomeAssetContext();
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = {
    ...offer,
    orderValue: currentValue || 0,
    orderFee: selectedFee,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };

  const { Button, isSelected, forceLoading } = infoGetter(
    'fixedIncome',
    completeOffer,
    'productIdNormalized'
  );

  const onOpenDetailsModal = () => {
    setModalOpen(true);
    setOfferSelected(offer);
    setOfferFeeSelected(selectedFee);
  };

  if (forceLoading) {
    return <ListLoadingItemDesktop />;
  }

  const infosProps = {
    offer: completeOffer,
    selectedFee,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };

  return (
    <TableContent
      direction="column"
      space="stack.xxxs"
      p={0}
      px="inline.xxxs"
      py="stack.xxxs"
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <HStack justifyContent="space-between" w="full">
        <Stack w="1/6">
          <HStack space="inline.xxxs" alignItems="center">
            <UI.Tooltip testID="details-tooltip" label={productsDetailText}>
              <Pressable onPress={onOpenDetailsModal}>
                <UI.Icon name="Info" size="xs" />
              </Pressable>
            </UI.Tooltip>
            <VStack pr="inline.xxxs" space="stack.quarck" flex={1}>
              <TableContentItemText ellipsizeMode="tail" tooltip={offer.productDisplayNormalized}>
                {offer.productDisplayNormalized}
              </TableContentItemText>
              <TableContentItemCaptions icons={offer.captions} />
            </VStack>
          </HStack>
        </Stack>
        <ApplicationInfo w="1/6" {...infosProps} />
        <RiskInfo w="1/6" {...infosProps} />
        <TaxInfo w="1/6" {...infosProps} />
        <ProfitabilityInfo w="1/6" {...infosProps} />
        <MaturityInfo w="1/6" {...infosProps} />
      </HStack>
      <HStack justifyContent="space-between" w="full">
        <Stack w="1/6">
          <VStack space="stack.quarck" justifyContent="space-between">
            <HStack space="inline.quarck" alignItems="center">
              <UI.Text variant="semibold">Taxa</UI.Text>
              <UI.Text fontSize="xs">(Spread)</UI.Text>
            </HStack>
            <Stack mr="inline.xxxs" bgColor="backgroundshade" borderRadius={BoxBorder.thick}>
              <UI.Select
                triggerProps={{
                  isDisabled: !ableToChangeFees,
                  _disabled: { opacity: 0.2 },
                }}
                testID="fee-select"
                options={offer.distributionFeesNormalized.map((feeOption) => ({
                  label: feeOption.label,
                  value: feeOption.feeId.toString(),
                }))}
                disabled={isSelected}
                placeholder="--"
                selectedValue={selectedFeeId?.toString() || ''}
                onValueChange={(value) => setSelectedFeeId(Number(value))}
              />
            </Stack>
          </VStack>
        </Stack>
        <UnitPriceInfo w="1/6" {...infosProps} />
        <FeeInfo w="1/6" {...infosProps} />
        <Stack w="1/6">
          <VStack space="stack.quarck" justifyContent="space-between">
            <UI.Text variant="semibold">Valor a aplicar</UI.Text>
            <Stack pr="inline.xxxs">
              <InputMoney
                id={`fixedIncome-input${id}`}
                name="createAppliedAmount"
                control={control}
                handleFocus={handleInputFocus}
                handleBlur={handleInputBlur}
                handleSubmit={handleSubmit}
                handleClear={handleInputClear}
                isDisabled={isSelected}
                currentValue={currentValueFormatted}
                hasFocus={isFocused}
                minValue={minApplication}
                customInputProps={{
                  ...{
                    ...(showMessageAdjustUnitPrice && {
                      helpTextProps,
                      helpText,
                    }),
                  },
                  ...{ ...(isFocused && hasError && { error: hasError }) },
                  ...{ ...(isFocused && !hasError && { helpText }) },
                }}
              />
              {showMessageAdjustUnitPrice && !isFocused && (
                <TableContentItemText fontSize="xs" color="neutral.light">
                  Ajustado por preço unitário
                </TableContentItemText>
              )}
            </Stack>
          </VStack>
        </Stack>
        <NetValueInfo w="1/6" {...infosProps} />
        <Stack w="1/6">
          <Box w="100%">
            <Button
              id={selectedTab.typeGetterId}
              offer={completeOffer}
              isDisabled={typeof hasError?.message === 'string'}
              productContextConstant={selectedTab}
            />
          </Box>
        </Stack>
      </HStack>
    </TableContent>
  );
};
